/**
 * Session selection algorithm (SPEC §7.4).
 *
 *   pool  = exercises[level].filter(not mastered)
 *   prio  = shuffle(pool.filter(wrongCount > 0))
 *   rest  = shuffle(pool.filter(wrongCount == 0))
 *   queue = (prio ++ rest).slice(0, settings.sessionSize)
 *
 * Empty pool → completed screen. Queue is fixed for the session
 * (a wrongly answered item does not repeat within the same session).
 */
import { shuffle } from './shuffle'
import type { Exercise, StorageState } from './types'

export interface SessionQueue {
  level: number
  total: number                  // full pool size (for progress ring on home)
  queue: Exercise[]              // fixed for this session
  queueIndex: number             // pointer; -1 means "not started"
  good: string[]                 // ids answered correctly
  bad: string[]                  // ids answered incorrectly
}

export function buildSession(
  exercises: readonly Exercise[],
  state: StorageState,
  level: number,
): SessionQueue | null {
  const pool = exercises.filter((e) => e.level === level)
  const total = pool.length
  const notMastered = pool.filter((e) => !state.mastered[e.id])
  if (notMastered.length === 0) return null  // completed

  const wrongFirst = notMastered.filter((e) => (state.wrong[e.id] ?? 0) > 0)
  const clean = notMastered.filter((e) => !state.wrong[e.id])
  const sessionSize = state.settings.sessionSize
  const queue = [...shuffle(wrongFirst), ...shuffle(clean)].slice(0, sessionSize)

  return {
    level,
    total,
    queue,
    queueIndex: -1,
    good: [],
    bad: [],
  }
}

export function advance(
  sess: SessionQueue,
  result: 'good' | 'bad',
): SessionQueue {
  const id = sess.queue[sess.queueIndex]?.id
  if (!id) return sess
  return {
    ...sess,
    good: result === 'good' ? [...sess.good, id] : sess.good,
    bad: result === 'bad' ? [...sess.bad, id] : sess.bad,
  }
}

export function isComplete(sess: SessionQueue): boolean {
  return sess.queueIndex >= sess.queue.length - 1
}

/** Progress fraction (0..1) for the home page ring. */
export function levelProgress(
  poolSize: number,
  masteredCount: number,
): number {
  if (poolSize <= 0) return 0
  return Math.min(1, masteredCount / poolSize)
}
